import React, { useState } from "react";
import { DialogLayout } from "../ui/layouts/DialogLayout";
import { Button } from "../ui/components/Button";
import { Badge } from "../ui/components/Badge";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import { FeatherRepeat } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherEdit2 } from "@subframe/core";
import { FeatherArrowRight } from "@subframe/core";
import { FeatherCheck } from "@subframe/core";
import { FeatherCircle } from "@subframe/core";

export interface JournalChange {
  field: string;
  oldValue: string;
  newValue: string;
  label: string;
}

interface ConfirmEditJournalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: JournalChange[];
  onConfirm: (updateAll: boolean) => void;
  loading?: boolean;
}

const ConfirmEditJournalModal: React.FC<ConfirmEditJournalModalProps> = ({
  open,
  onOpenChange,
  changes,
  onConfirm,
  loading = false,
}) => {
  const [updateOption, setUpdateOption] = useState<"this" | "all">("this");

  const handleConfirm = () => {
    onConfirm(updateOption === "all");
  };

  const handleClose = () => {
    setUpdateOption("this");
    onOpenChange(false);
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-192 flex-none flex-col items-start gap-6 rounded-lg border border-solid border-neutral-border bg-default-background px-8 py-6 shadow-lg">
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-start gap-3">
            <IconWithBackground
              variant="warning"
              size="large"
              icon={<FeatherRepeat />}
            />
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Update Journal Entry
              </span>
              <span className="text-body font-body text-subtext-color">
                This is a recurring journal entry. Choose how you want to apply
                these changes.
              </span>
            </div>
            <FeatherX
              className="text-heading-3 font-heading-3 text-default-font cursor-pointer"
              onClick={handleClose}
            />
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
          <div className="flex items-center gap-2">
            <FeatherEdit2 className="text-body font-body text-subtext-color" />
            <span className="text-heading-3 font-heading-3 text-default-font">
              Changes Summary
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-3">
            {changes.map((change, index) => (
              <div
                key={index}
                className="flex w-full items-start gap-4 rounded-md border border-solid border-neutral-200 bg-white px-4 py-4"
              >
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="neutral">{change.label}</Badge>
                    <span className="text-caption font-caption text-subtext-color">
                      Modified
                    </span>
                  </div>
                  {change.field === "notes" ? (
                    <div className="flex w-full flex-col items-start gap-2">
                      <span className="text-body font-body text-neutral-400 line-through">
                        {change.oldValue}
                      </span>
                      <div className="flex items-center gap-2">
                        <FeatherArrowRight className="text-caption font-caption text-subtext-color" />
                        <span className="text-body-bold font-body-bold text-default-font">
                          {change.newValue}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full items-center gap-2">
                      <span className="text-body font-body text-neutral-400 line-through">
                        {change.oldValue}
                      </span>
                      <FeatherArrowRight className="text-caption font-caption text-subtext-color" />
                      <span className="text-body-bold font-body-bold text-default-font">
                        {change.newValue}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-3">
          <span className="text-body-bold font-body-bold text-default-font">
            Apply changes to:
          </span>
          <div
            className={`flex w-full items-start gap-4 rounded-md border border-solid ${
              updateOption === "this"
                ? "border-brand-600 bg-brand-50"
                : "border-neutral-border bg-neutral-50"
            } px-6 py-6 cursor-pointer`}
            onClick={() => setUpdateOption("this")}
          >
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Update Only This Entry
              </span>
              <span className="text-body font-body text-subtext-color">
                Apply changes to this journal entry only. Future recurring
                entries will remain unchanged.
              </span>
            </div>
            {updateOption === "this" ? (
              <FeatherCheck className="text-heading-3 font-heading-3 text-brand-600" />
            ) : (
              <FeatherCircle className="text-heading-3 font-heading-3 text-neutral-400" />
            )}
          </div>
          <div
            className={`flex w-full items-start gap-4 rounded-md border border-solid ${
              updateOption === "all"
                ? "border-brand-600 bg-brand-50"
                : "border-neutral-border bg-neutral-50"
            } px-6 py-6 cursor-pointer`}
            onClick={() => setUpdateOption("all")}
          >
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Update All Future Entries
              </span>
              <span className="text-body font-body text-subtext-color">
                Apply changes to this entry and all future recurring journal
                entries in the series.
              </span>
            </div>
            {updateOption === "all" ? (
              <FeatherCheck className="text-heading-3 font-heading-3 text-brand-600" />
            ) : (
              <FeatherCircle className="text-heading-3 font-heading-3 text-neutral-400" />
            )}
          </div>
        </div>
        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
        <div className="flex w-full items-center justify-between">
          <Button
            variant="neutral-tertiary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            icon={<FeatherCheck />}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? "Updating..." : "Confirm Update"}
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ConfirmEditJournalModal;
